import { computed, watch, type Ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { projectFns } from "../convexClient";
import { useChatQuery } from "./useChatQuery";
import { useThreadMessages } from "./useThreadMessages";

export function useProjectMessages(projectId: Ref<number>) {
  const queryClient = useQueryClient();
  const thread = useChatQuery(projectFns.getProjectThreadId, computed(() => projectId.value > 0 ? { project_id: projectId.value } : null));
  const threadId = computed(() => thread.data.value ?? null);
  const chat = useThreadMessages(threadId);
  watch(() => chat.messages.value[chat.messages.value.length - 1]?._id, (_id, _previous, onCleanup) => {
    if (!chat.messages.value[chat.messages.value.length - 1]?.attachments?.length) return;
    void queryClient.invalidateQueries({ queryKey: ["v2projects", "detail", projectId.value] });
    void queryClient.invalidateQueries({ queryKey: ["v2projects", "attachments", projectId.value] });
    const id = projectId.value;
    const timer = window.setTimeout(() => {
      if (projectId.value === id) void queryClient.invalidateQueries({ queryKey: ["v2projects", "attachments", id] });
    }, 2000);
    onCleanup(() => window.clearTimeout(timer));
  });
  return {
    ...chat, threadId,
    loadError: computed(() => thread.error.value ? "Unable to open conversation. Please try again." : chat.loadError.value),
    // A project without a Convex chatroom yet resolves to a null thread; show the empty state instead of loading forever.
    isReady: computed(() => !!thread.error.value || thread.data.value === null || chat.isReady.value),
    refresh: () => { thread.refresh(); chat.refresh(); },
  };
}
